import { ReactElement, useState } from 'react';
import { OrientationContent } from './styles';
import { useTranslation } from 'react-i18next';
import { FileButton } from './components/FileButton';
import { ValidationTable } from './components/ValidationTable';
import { useToast } from '~/contexts/newToastContext';
import {
  apiCall, apiCallFormData, ApiParams, ApiResps,
} from 'providers';
import { useParams } from 'react-router-dom';
import { ValidationInfo } from './components/ValidationInfo';
import { FileOptions } from './components/FileOptions';
import { GoalsBatch } from 'providers/types/api-private';

export function SpreadsheetInsert(): ReactElement {
  const [file, setFile] = useState<File>();
  const [loading, setLoading] = useState(false);
  const [loadingValidation, setLoadingValidation] = useState(false);
  const [validatedList, setValidatedList] = useState<ApiResps['/energy/check-goals-batch']>();

  const { toast } = useToast();
  const { t } = useTranslation();
  const { clientId } = useParams<{ clientId: string }>();

  async function onChangeFile(newFile: File): Promise<void> {
    if (newFile) {
      const valid = await validateFile(newFile);
      if (!valid) return;
    }

    setFile(newFile);
  }

  async function validateFile(file: File): Promise<boolean> {
    try {
      setLoadingValidation(true);

      const response = await apiCallFormData('/energy/check-goals-batch', {
        CLIENT_ID: Number(clientId),
      }, {
        file,
      });

      setValidatedList(response);
      setLoadingValidation(false);
      return true;
    } catch (error) {
      console.error(error);
      toast(t('erroTitulo'), t('erroAnalisarDadosInseridos'), 'error');
      setLoadingValidation(false);
      return false;
    }
  }

  function onCancel(): void {
    setFile(undefined);
  }

  function onResendFile(file: File) {
    onCancel();
    onChangeFile(file);
  }

  async function onSubmitFile(): Promise<void> {
    try {
      setLoading(true);
      if (!file || !validatedList?.LIST) {
        toast(t('erroTitulo'), t('erroSalvarInformacoes'), 'error');
        return;
      }

      const response = await batchAddGoalsData(Number(clientId), parseValidatedListInputData());

      if (response.ADDED.length > 0) {
        toast(t('sucessoTitulo'), t('sucessoSalvarInformacoes'), 'success');
      } else if (response.IGNORED.length > 0) {
        const errorMessages = response.IGNORED.map((item) => `${t('registroIgnorado')} ${item.REASON}\n`);
        toast(t('erroTitulo'), errorMessages.join(', '), 'error');
      }

      setLoading(false);
      setFile(undefined);
    } catch (error) {
      console.error(error);
      toast(t('erroTitulo'), t('erroSalvarInformacoes'), 'error');
    }
  }

  async function batchAddGoalsData(clientId: number, payload: GoalsBatch[]): Promise<ApiResps['/energy/add-goals-batch']> {
    const BATCH_SIZE = 100;
    const response: ApiResps['/energy/add-goals-batch'] = {
      ADDED: [],
      IGNORED: [],
    };

    for (let i = 0; i < payload.length; i += BATCH_SIZE) {
      const batch = payload.slice(i, i + BATCH_SIZE);
      const batchResponse = await apiCall('/energy/add-goals-batch', {
        CLIENT_ID: clientId,
        DATA: batch,
      });
      response.ADDED.push(...batchResponse.ADDED);
      response.IGNORED.push(...batchResponse.IGNORED);
    }

    return response;
  }

  function parseValidatedListInputData(): ApiParams['/energy/add-goals-batch']['DATA'] {
    return validatedList?.LIST.map((item) => {
      const { ERRORS: _, ...values } = item;

      return values;
    }) ?? [];
  }

  function validatedUnitsInfo(list: ApiResps['/energy/check-goals-batch']['LIST']): string {
    return list.map((item) => (!item.UNIT_ID || !item.UNIT_NAME ? '' : `${item.UNIT_NAME ?? ''} (${item.UNIT_ID ?? '-'})`)).join(', ');
  }

  const hasValidationErrors = !!validatedList?.LIST.some((item) => item.ERRORS.length > 0, [validatedList]);

  return file ? (
    <>
      <OrientationContent>
        <h3>{t('enviarArquivo')}</h3>
        <span>
          {t('arquivoAtualEstaPreenchendoDadosUnidades')}
          {' '}
          {validatedUnitsInfo(validatedList?.LIST ?? [])}
        </span>
        <span>{t('desejaFazerEnvioDados')}</span>
      </OrientationContent>
      <FileOptions
        error={hasValidationErrors}
        file={file}
        loading={loading}
        onClickSubmit={onSubmitFile}
        onClickCancel={onCancel}
        onClickResendFile={onResendFile}
      />
      {hasValidationErrors && (
        <ValidationInfo validatedList={validatedList?.LIST ?? []} />
      )}
      <ValidationTable validatedList={validatedList?.LIST ?? []} />
    </>
  ) : (
    <FileButton onChangeFile={onChangeFile} disabled={loadingValidation} />
  );
}
